<template>
  <div
    v-if="hasOverflow"
    class="table-scroll-navigator-root"
    :class="{
      'is-rtl': isRTL,
      'is-ltr': !isRTL,
      'is-scrolling': isScrolling,
      'is-pinned': isSticky,
      'is-past-table': isPastTable,
    }"
    ref="rootEl"
  >
    <!-- Left Edge Glow & Floating Jump Button -->
    <transition name="nav-fade">
      <div
        v-if="showLeftEdge && !isPastTable"
        class="table-edge-control edge-left"
        :class="{ 'edge-sticky': isSticky }"
        :title="isRTL ? $t('table.goToEnd') : $t('table.goToStart')"
        @click="isRTL ? scrollToRatio(1) : scrollToRatio(0)"
      >
        <div class="edge-mask"></div>
        <button
          type="button"
          class="edge-btn"
          :aria-label="isRTL ? 'End' : 'Start'"
        >
          <i
            :class="
              isRTL ? 'fal fa-angle-double-left' : 'fal fa-angle-double-left'
            "
          ></i>
        </button>
      </div>
    </transition>

    <!-- Right Edge Glow & Floating Jump Button -->
    <transition name="nav-fade">
      <div
        v-if="showRightEdge && !isPastTable"
        class="table-edge-control edge-right"
        :class="{ 'edge-sticky': isSticky }"
        :title="isRTL ? $t('table.goToStart') : $t('table.goToEnd')"
        @click="isRTL ? scrollToRatio(0) : scrollToRatio(1)"
      >
        <div class="edge-mask"></div>
        <button
          type="button"
          class="edge-btn"
          :aria-label="isRTL ? 'Start' : 'End'"
        >
          <i
            :class="
              isRTL ? 'fal fa-angle-double-right' : 'fal fa-angle-double-right'
            "
          ></i>
        </button>
      </div>
    </transition>

    <!-- Top Floating & Sticky HUD Mini-Dock Controller -->
    <transition name="hud-slide">
      <div
        v-if="!isPastTable"
        class="table-hud-pill-dock"
        :class="{
          'dock-expanded': isInteracting,
          'dock-pinned': isSticky,
        }"
      >
        <!-- Jump to Start Button -->
        <button
          type="button"
          class="hud-btn hud-jump-start"
          :class="{ disabled: isAtStart }"
          :title="$t('table.goToStart')"
          @click="scrollToStart"
        >
          <i class="fal fa-fast-backward" v-if="!isRTL"></i>
          <i class="fal fa-fast-forward" v-else></i>
          <span class="hud-btn-label">{{ $t("table.tableStart") }}</span>
        </button>

        <!-- Step Backward Button -->
        <button
          type="button"
          class="hud-btn hud-step"
          :class="{ disabled: isAtStart }"
          :title="isRTL ? $t('table.scrollRight') : $t('table.scrollLeft')"
          @click="stepScroll(isRTL ? 1 : -1)"
        >
          <i
            :class="isRTL ? 'fal fa-chevron-right' : 'fal fa-chevron-left'"
          ></i>
        </button>

        <!-- Interactive Scrub Bar / Radar Track -->
        <div
          class="hud-track-container"
          ref="trackEl"
          @mousedown="onTrackMouseDown"
          @touchstart.passive="onTrackTouchStart"
          :title="$t('table.scrollTip')"
        >
          <div class="hud-track-bg">
            <div
              class="hud-track-fill"
              :style="{
                width: `${Math.max(12, viewportRatio * 100)}%`,
                [isRTL ? 'right' : 'left']: `${
                  scrollPercent * (100 - Math.max(12, viewportRatio * 100))
                }%`,
              }"
            >
              <span class="hud-track-glow"></span>
            </div>
          </div>
        </div>

        <!-- Step Forward Button -->
        <button
          type="button"
          class="hud-btn hud-step"
          :class="{ disabled: isAtEnd }"
          :title="isRTL ? $t('table.scrollLeft') : $t('table.scrollRight')"
          @click="stepScroll(isRTL ? -1 : 1)"
        >
          <i
            :class="isRTL ? 'fal fa-chevron-left' : 'fal fa-chevron-right'"
          ></i>
        </button>

        <!-- Jump to End Button -->
        <button
          type="button"
          class="hud-btn hud-jump-end"
          :class="{ disabled: isAtEnd }"
          :title="$t('table.goToEnd')"
          @click="scrollToEnd"
        >
          <span class="hud-btn-label">{{ $t("table.tableEnd") }}</span>
          <i class="fal fa-fast-forward" v-if="!isRTL"></i>
          <i class="fal fa-fast-backward" v-else></i>
        </button>

        <!-- Percentage / Status Badge -->
        <div class="hud-status-badge">
          <span v-if="isAtStart" class="badge-tag start">{{
            $t("table.tableStart")
          }}</span>
          <span v-else-if="isAtEnd" class="badge-tag end">{{
            $t("table.tableEnd")
          }}</span>
          <span v-else class="badge-tag mid"
            >{{ Math.round(scrollPercent * 100) }}%</span
          >
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "TableScrollNavigator",
  props: {
    target: {
      type: [String, Object, HTMLElement],
      default: null,
    },
    stepSize: {
      type: Number,
      default: 260,
    },
    enableDragToPan: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hasOverflow: false,
      scrollPercent: 0,
      viewportRatio: 0.3,
      isAtStart: true,
      isAtEnd: false,
      showLeftEdge: false,
      showRightEdge: false,
      isScrolling: false,
      isInteracting: false,
      isSticky: false,
      isPastTable: false,
      scrollTimer: null,
      wrapperEl: null,
      resizeObserver: null,
      mutationObserver: null,
      rtlScrollType: "negative",
      isDraggingTrack: false,
      isDraggingTable: false,
      startX: 0,
      startScrollLeft: 0,
    };
  },
  computed: {
    isRTL() {
      if (this.$vuetify && this.$vuetify.rtl !== undefined) {
        return this.$vuetify.rtl;
      }
      return this.$store?.getters["lang_module/lang"] === "ar";
    },
  },
  watch: {
    isRTL() {
      this.$nextTick(() => {
        this.detectRTLType();
        this.checkOverflowAndScroll();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initNavigator();
    });
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    initNavigator() {
      this.resolveWrapper();
      if (!this.wrapperEl) {
        setTimeout(() => {
          this.resolveWrapper();
          if (this.wrapperEl) this.attachListeners();
        }, 300);
        return;
      }
      this.attachListeners();
    },

    resolveWrapper() {
      if (this.target) {
        if (typeof this.target === "string") {
          const el = document.querySelector(this.target);
          this.wrapperEl = el?.classList.contains("v-data-table__wrapper")
            ? el
            : el?.querySelector(".v-data-table__wrapper") || el;
        } else if (this.target instanceof HTMLElement) {
          this.wrapperEl = this.target.classList.contains(
            "v-data-table__wrapper"
          )
            ? this.target
            : this.target.querySelector(".v-data-table__wrapper") ||
              this.target;
        }
      }

      if (!this.wrapperEl && this.$el) {
        const parent = this.$el.closest(
          ".magic-table-wrapper, .v-data-table, .wrraper"
        );
        if (parent) {
          this.wrapperEl =
            parent.querySelector(".v-data-table__wrapper") ||
            parent.querySelector("table")?.parentElement ||
            parent;
        }
      }
    },

    attachListeners() {
      if (!this.wrapperEl) return;

      this.detectRTLType();
      this.wrapperEl.addEventListener("scroll", this.onScroll, {
        passive: true,
      });
      window.addEventListener("scroll", this.onWindowScroll, { passive: true });

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.checkOverflowAndScroll();
        });
        this.resizeObserver.observe(this.wrapperEl);
        const table = this.wrapperEl.querySelector("table");
        if (table) this.resizeObserver.observe(table);
      } else {
        window.addEventListener("resize", this.checkOverflowAndScroll, {
          passive: true,
        });
      }

      if (window.MutationObserver) {
        this.mutationObserver = new MutationObserver(() => {
          this.checkOverflowAndScroll();
        });
        this.mutationObserver.observe(this.wrapperEl, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }

      if (this.enableDragToPan) {
        this.initTablePan();
      }

      this.checkOverflowAndScroll();
      this.onWindowScroll();
    },

    onWindowScroll() {
      if (!this.wrapperEl) return;
      const rect = this.wrapperEl.getBoundingClientRect();
      const topOffset = 75; // Account for navbar height

      // Table is currently passing under the sticky top area
      this.isSticky = rect.top < topOffset && rect.bottom > topOffset + 100;
      this.isPastTable = rect.bottom <= topOffset + 100;
    },

    detectRTLType() {
      if (!this.isRTL || !this.wrapperEl) return;
      const cur = this.wrapperEl.scrollLeft;
      if (cur < 0) {
        this.rtlScrollType = "negative";
      } else if (cur > 0) {
        this.rtlScrollType = "positive";
      } else {
        this.rtlScrollType = "negative";
      }
    },

    checkOverflowAndScroll() {
      if (!this.wrapperEl) return;
      const { scrollWidth, clientWidth } = this.wrapperEl;
      const maxScroll = scrollWidth - clientWidth;
      this.hasOverflow = maxScroll > 8;

      if (!this.hasOverflow) {
        this.scrollPercent = 0;
        this.isAtStart = true;
        this.isAtEnd = true;
        this.showLeftEdge = false;
        this.showRightEdge = false;
        return;
      }

      this.viewportRatio = Math.min(
        1,
        Math.max(0.15, clientWidth / scrollWidth)
      );
      this.updateScrollMetrics(maxScroll);
    },

    updateScrollMetrics(maxScroll) {
      if (!this.wrapperEl) return;
      if (maxScroll === undefined) {
        maxScroll = this.wrapperEl.scrollWidth - this.wrapperEl.clientWidth;
      }
      if (maxScroll <= 0) return;

      const rawScroll = this.wrapperEl.scrollLeft;
      let ratio = 0;

      if (!this.isRTL) {
        ratio = Math.max(0, Math.min(1, rawScroll / maxScroll));
        this.isAtStart = rawScroll <= 6;
        this.isAtEnd = rawScroll >= maxScroll - 6;
        this.showLeftEdge = !this.isAtStart;
        this.showRightEdge = !this.isAtEnd;
      } else {
        if (this.rtlScrollType === "negative" || rawScroll <= 0) {
          const scrolled = Math.abs(rawScroll);
          ratio = Math.max(0, Math.min(1, scrolled / maxScroll));
          this.isAtStart = scrolled <= 6;
          this.isAtEnd = scrolled >= maxScroll - 6;
        } else {
          ratio = Math.max(0, Math.min(1, rawScroll / maxScroll));
          this.isAtStart = rawScroll <= 6;
          this.isAtEnd = rawScroll >= maxScroll - 6;
        }
        this.showRightEdge = !this.isAtStart;
        this.showLeftEdge = !this.isAtEnd;
      }

      this.scrollPercent = ratio;
    },

    onScroll() {
      this.isScrolling = true;
      this.checkOverflowAndScroll();

      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false;
      }, 400);
    },

    scrollToRatio(ratio, smooth = true) {
      if (!this.wrapperEl) return;
      const { scrollWidth, clientWidth } = this.wrapperEl;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) return;

      const clampedRatio = Math.max(0, Math.min(1, ratio));
      let targetLeft = 0;

      if (!this.isRTL) {
        targetLeft = clampedRatio * maxScroll;
      } else {
        if (
          this.rtlScrollType === "negative" ||
          this.wrapperEl.scrollLeft <= 0
        ) {
          targetLeft = -(clampedRatio * maxScroll);
        } else {
          targetLeft = clampedRatio * maxScroll;
        }
      }

      try {
        this.wrapperEl.scrollTo({
          left: targetLeft,
          behavior: smooth ? "smooth" : "auto",
        });
      } catch (e) {
        this.wrapperEl.scrollLeft = targetLeft;
      }
    },

    scrollToStart() {
      this.scrollToRatio(0, true);
    },

    scrollToEnd() {
      this.scrollToRatio(1, true);
    },

    stepScroll(direction) {
      if (!this.wrapperEl) return;
      const step = this.stepSize * direction;
      try {
        this.wrapperEl.scrollBy({
          left: step,
          behavior: "smooth",
        });
      } catch (e) {
        this.wrapperEl.scrollLeft += step;
      }
    },

    onTrackMouseDown(e) {
      if (e.button !== 0) return;
      this.isDraggingTrack = true;
      this.isInteracting = true;
      this.handleTrackEvent(e);

      const onMouseMove = (ev) => {
        if (!this.isDraggingTrack) return;
        this.handleTrackEvent(ev);
      };

      const onMouseUp = () => {
        this.isDraggingTrack = false;
        this.isInteracting = false;
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },

    onTrackTouchStart(e) {
      this.isDraggingTrack = true;
      this.isInteracting = true;
      const touch = e.touches[0];
      if (touch) this.handleTrackEvent(touch);

      const onTouchMove = (ev) => {
        if (!this.isDraggingTrack) return;
        const t = ev.touches[0];
        if (t) this.handleTrackEvent(t);
      };

      const onTouchEnd = () => {
        this.isDraggingTrack = false;
        this.isInteracting = false;
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("touchend", onTouchEnd);
      };

      window.addEventListener("touchmove", onTouchMove, { passive: true });
      window.addEventListener("touchend", onTouchEnd);
    },

    handleTrackEvent(e) {
      const track = this.$refs.trackEl;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      let ratio = clickX / rect.width;

      if (this.isRTL) {
        ratio = 1 - ratio;
      }

      this.scrollToRatio(ratio, false);
    },

    initTablePan() {
      if (!this.wrapperEl) return;
      const header = this.wrapperEl.querySelector("thead");
      const targetPanArea = header || this.wrapperEl;

      const onMouseDown = (e) => {
        if (
          e.target.closest(
            "button, input, a, .v-icon, .v-checkbox, .v-chip, ._actions"
          ) ||
          e.button !== 0
        ) {
          return;
        }

        this.isDraggingTable = true;
        this.startX = e.pageX - this.wrapperEl.offsetLeft;
        this.startScrollLeft = this.wrapperEl.scrollLeft;
        targetPanArea.style.cursor = "grabbing";
        targetPanArea.style.userSelect = "none";

        const onMouseMove = (ev) => {
          if (!this.isDraggingTable) return;
          ev.preventDefault();
          const x = ev.pageX - this.wrapperEl.offsetLeft;
          const walk = (x - this.startX) * 1.5;
          this.wrapperEl.scrollLeft = this.startScrollLeft - walk;
        };

        const onMouseUp = () => {
          this.isDraggingTable = false;
          targetPanArea.style.cursor = "";
          targetPanArea.style.removeProperty("user-select");
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      };

      targetPanArea.addEventListener("mousedown", onMouseDown);
    },

    cleanup() {
      if (this.wrapperEl) {
        this.wrapperEl.removeEventListener("scroll", this.onScroll);
      }
      window.removeEventListener("scroll", this.onWindowScroll);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      window.removeEventListener("resize", this.checkOverflowAndScroll);
      clearTimeout(this.scrollTimer);
    },
  },
};
</script>

<style lang="scss" scoped>
.table-scroll-navigator-root {
  position: sticky;
  top: 75px; // Sits nicely below navbar when scrolling down
  width: 100%;
  pointer-events: none;
  z-index: 25;
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &.is-past-table {
    opacity: 0;
    pointer-events: none;
  }
}

/* Side Edge Glow Masks & Quick Jump Floating Buttons */
.table-edge-control {
  position: absolute;
  top: 45px; // Extends down into table body
  bottom: -480px;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  cursor: pointer;
  z-index: 22;
  transition: opacity 0.3s ease, transform 0.3s ease;

  &.edge-left {
    left: 0;
    .edge-mask {
      background: linear-gradient(
        90deg,
        rgba(120, 61, 115, 0.16) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  &.edge-right {
    right: 0;
    .edge-mask {
      background: linear-gradient(
        270deg,
        rgba(120, 61, 115, 0.16) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  .edge-mask {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 12px;
    pointer-events: none;
    opacity: 0.85;
  }

  .edge-btn {
    position: relative;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid rgba(120, 61, 115, 0.2);
    color: var(--mainColor-2, #783d73);
    box-shadow: 0 4px 14px rgba(120, 61, 115, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    outline: none;

    &:hover {
      transform: scale(1.15);
      background: var(--mainColor-2, #783d73);
      color: #ffffff;
      box-shadow: 0 6px 20px rgba(120, 61, 115, 0.4);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  &.edge-sticky {
    top: 10px;
  }
}

/* Floating & Sticky HUD Mini-Dock at TOP of Table */
.table-hud-pill-dock {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(120, 61, 115, 0.16);
  border-radius: 30px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.05), 0 2px 8px rgba(120, 61, 115, 0.08);
  pointer-events: auto;
  margin: 6px auto;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover,
  &.dock-expanded {
    box-shadow: 0 8px 24px rgba(120, 61, 115, 0.18),
      0 4px 12px rgba(0, 0, 0, 0.08);
    background: #ffffff;
    transform: translateY(-2px);
  }

  /* When page is scrolled down and HUD is pinned */
  &.dock-pinned {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(120, 61, 115, 0.3);
    box-shadow: 0 10px 30px rgba(120, 61, 115, 0.22),
      0 2px 10px rgba(0, 0, 0, 0.08);
    transform: translateY(2px) scale(1.02);
  }

  /* HUD Buttons */
  .hud-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    height: 30px;
    border-radius: 20px;
    border: none;
    background: #f8fafc;
    color: var(--mainColor-2, #783d73);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    outline: none;
    transition: all 0.2s ease;
    white-space: nowrap;

    i {
      font-size: 12px;
      transition: transform 0.2s ease;
    }

    &:hover:not(.disabled) {
      background: var(--mainColor-2, #783d73);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(120, 61, 115, 0.25);
      transform: translateY(-1px);

      i {
        transform: scale(1.1);
      }
    }

    &:active:not(.disabled) {
      transform: translateY(0);
    }

    &.disabled {
      opacity: 0.38;
      cursor: not-allowed;
      pointer-events: none;
    }

    &.hud-step {
      width: 30px;
      padding: 0;
      justify-content: center;
      border-radius: 50%;
    }

    &.hud-jump-start,
    &.hud-jump-end {
      background: rgba(120, 61, 115, 0.08);
    }
  }

  /* Interactive Scrub Bar / Radar Track */
  .hud-track-container {
    position: relative;
    width: 130px;
    height: 22px;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0 4px;
    user-select: none;

    .hud-track-bg {
      position: relative;
      width: 100%;
      height: 7px;
      background: #e2e8f0;
      border-radius: 6px;
      overflow: hidden;
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    .hud-track-fill {
      position: absolute;
      top: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        var(--mainColor-2, #783d73) 0%,
        #9b4f95 100%
      );
      border-radius: 6px;
      box-shadow: 0 0 8px rgba(120, 61, 115, 0.45);
      transition: width 0.15s ease;

      .hud-track-glow {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.4),
          transparent
        );
        animation: trackShimmer 2s infinite ease-in-out;
      }
    }

    &:hover .hud-track-bg {
      background: #cbd5e1;
    }
  }

  /* Status Badge */
  .hud-status-badge {
    display: inline-flex;
    align-items: center;

    .badge-tag {
      font-size: 11px;
      font-weight: 800;
      padding: 2px 9px;
      border-radius: 12px;
      letter-spacing: 0.2px;

      &.start {
        background: rgba(16, 185, 129, 0.12);
        color: #10b981;
      }

      &.end {
        background: rgba(239, 68, 68, 0.12);
        color: #ef4444;
      }

      &.mid {
        background: rgba(120, 61, 115, 0.1);
        color: var(--mainColor-2, #783d73);
      }
    }
  }
}

/* Transitions */
.nav-fade-enter-active,
.nav-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.nav-fade-enter,
.nav-fade-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.hud-slide-enter-active,
.hud-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.hud-slide-enter,
.hud-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes trackShimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .table-hud-pill-dock {
    gap: 4px;
    padding: 4px 8px;

    .hud-btn-label {
      display: none;
    }

    .hud-track-container {
      width: 80px;
    }
  }
}
</style>
